import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import getRenewalSchedule from '@/utils/getRenewalSchedule';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { TakerMarket } from '../data/useTakerMarkets';
import useUpdatePreferredAutoRenewal from '../data/useUpdatePreferredAutoRenewal';
import { RENEWAL_TERM_DAYS } from './OfferBuilder/OfferReview/PreferredAutoRenewal';

interface AutoRenewalModalProps {
  /**
   * Function to close the modal
   */
  onClose: () => void;
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * The taker market to toggle auto renewal for
   */
  takerMarket: TakerMarket;
}

const AutoRenewalModal = ({ onClose, open, takerMarket }: AutoRenewalModalProps) => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const showSnackbar = useSnackbar();
  const [showError, setShowError] = useState(false);
  const { hasRenewalSchedule, renewalIntervalDays } = getRenewalSchedule(takerMarket);
  const { mutateAsync: updatePreferredAutoRenewal, isPending } = useUpdatePreferredAutoRenewal();

  if (!takerMarket || !takerMarket.disableAfterMarketClearsDate) {
    return null;
  }

  const handleClose = () => {
    setShowError(false);
    onClose();
  };

  const handleEnableAutoRenewal = async () => {
    await updatePreferredAutoRenewal(
      {
        takerMarket,
        renewal: {
          enabled: true,
          termDays: RENEWAL_TERM_DAYS,
        },
      },
      {
        onSuccess: () => {
          handleClose();
          showSnackbar({ message: t('offer.autoRenew.enabled') });
        },
        onError: () => {
          setShowError(true);
        },
      }
    );
  };

  const handleDisableAutoRenewal = async () => {
    await updatePreferredAutoRenewal(
      {
        takerMarket,
        renewal: {
          enabled: false,
        },
      },
      {
        onSuccess: () => {
          handleClose();
          showSnackbar({ message: t('offer.autoRenew.disabled') });
        },
        onError: () => {
          setShowError(true);
        },
      }
    );
  };

  return (
    <Modal disableOutsideClickClose={isPending} onClose={handleClose} open={open} size="sm">
      <ModalTitleInverse>{t('offer.autoRenew.modifyAutoRenewal')}</ModalTitleInverse>
      <ModalContent className="space-y-4">
        {showError && (
          <Alert
            full
            title={
              hasRenewalSchedule ? t('offer.autoRenew.modal.disable.error') : t('offer.autoRenew.modal.enable.error')
            }
            type="error"
          />
        )}
        {hasRenewalSchedule ? (
          <div className="space-y-2">
            <p>
              <Trans
                i18nKey="offer.autoRenew.modal.disable.info"
                values={{
                  renewalIntervalDays,
                  disableAfterMarketClearsDate: asDateStringText({
                    date: takerMarket.disableAfterMarketClearsDate,
                    isUTC: false,
                    formatOptions: { month: 'long' },
                  }),
                }}
                components={{ bold: <strong /> }}
              />
            </p>
            <p>{t('offer.autoRenew.modal.disable.description')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p>{t('offer.autoRenew.modal.enable.info')}</p>
            <p>
              <Trans
                i18nKey="offer.autoRenew.modal.enable.description"
                values={{
                  renewalIntervalDays: RENEWAL_TERM_DAYS,
                  disableAfterMarketClearsDate: asDateStringText({
                    date: takerMarket.disableAfterMarketClearsDate,
                    isUTC: false,
                    formatOptions: { month: 'long' },
                  }),
                }}
                components={{ bold: <strong /> }}
              />
            </p>
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button onClick={handleClose} variant="secondary">
          {t('core.cancel')}
        </Button>
        {hasRenewalSchedule ? (
          <Button loading={isPending} onClick={handleDisableAutoRenewal} variant="destructive">
            {t('core.disable')}
          </Button>
        ) : (
          <Button loading={isPending} onClick={handleEnableAutoRenewal} variant="cta">
            {t('core.enable')}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default AutoRenewalModal;
